package se.mittpass.web.pages

import se.mittpass.web.*
import se.mittpass.web.components.*
import se.mittpass.web.pages.*
import se.mittpass.web.viewmodel.*
import se.mittpass.web.style.*


import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import androidx.compose.runtime.*
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.attributes.*
import se.mittpass.shared.api.AdminOrgDto
import se.mittpass.shared.api.AdminOverviewDto
import se.mittpass.shared.api.AdminUserDto
import se.mittpass.shared.api.AdminUpdateOrgRequest
import se.mittpass.shared.api.AdminUpdateUserRequest

private val adminScope = MainScope()

private enum class AdminView { ORGS, USERS }

@Composable
fun AdminPage() {
    var view by remember { mutableStateOf(AdminView.ORGS) }
    var overview by remember { mutableStateOf<AdminOverviewDto?>(null) }
    var orgs by remember { mutableStateOf<List<AdminOrgDto>>(emptyList()) }
    var users by remember { mutableStateOf<List<AdminUserDto>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var search by remember { mutableStateOf("") }
    var savingId by remember { mutableStateOf<Long?>(null) }

    var editingOrg by remember { mutableStateOf<AdminOrgDto?>(null) }
    var deletingOrg by remember { mutableStateOf<AdminOrgDto?>(null) }
    var editingUser by remember { mutableStateOf<AdminUserDto?>(null) }
    var deletingUser by remember { mutableStateOf<AdminUserDto?>(null) }

    fun reload() {
        loading = true
        error = null
        adminScope.launch {
            adminRepo.getOverview().onSuccess { overview = it }.onFailure { error = it.message }
            adminRepo.getOrgs().onSuccess { orgs = it }
            adminRepo.getUsers().onSuccess { users = it }
            loading = false
        }
    }

    LaunchedEffect(Unit) { reload() }

    // Org Edit Modal
    editingOrg?.let { org ->
        AdminEditOrgModal(
            org = org,
            onDismiss = { editingOrg = null },
            onSave = { name, orgNr ->
                editingOrg = null
                loading = true
                adminScope.launch {
                    adminRepo.updateOrg(org.id, AdminUpdateOrgRequest(name, orgNr))
                        .onFailure { error = it.message }
                    reload()
                }
            }
        )
    }

    // Org Delete Modal
    deletingOrg?.let { org ->
        AdminConfirmDeleteModal(
            title = "Radera organisation",
            message = "Är du helt säker på att du vill radera ${org.name}? All data (arbetsplatser, scheman, stämplingar, loggar och användare som bara tillhör denna organisation) raderas permanent!",
            confirmKeyword = org.name,
            onDismiss = { deletingOrg = null },
            onConfirm = {
                deletingOrg = null
                loading = true
                adminScope.launch {
                    adminRepo.deleteOrg(org.id).onFailure { error = it.message }
                    reload()
                }
            }
        )
    }

    // User Edit Modal
    editingUser?.let { user ->
        AdminEditUserModal(
            user = user,
            onDismiss = { editingUser = null },
            onSave = { fn, ln, email, role ->
                editingUser = null
                loading = true
                adminScope.launch {
                    adminRepo.updateUser(user.userId, AdminUpdateUserRequest(fn, ln, email, role))
                        .onFailure { error = it.message }
                    reload()
                }
            }
        )
    }

    // User Delete Modal
    deletingUser?.let { user ->
        val userName = listOfNotNull(user.firstName, user.lastName).joinToString(" ").ifBlank { user.email }
        AdminConfirmDeleteModal(
            title = "Radera användare",
            message = "Är du säker på att du vill radera användaren $userName (${user.email})? All personlig data och kopplingar raderas permanent!",
            confirmKeyword = user.email,
            onDismiss = { deletingUser = null },
            onConfirm = {
                deletingUser = null
                loading = true
                adminScope.launch {
                    adminRepo.deleteUser(user.userId).onFailure { error = it.message }
                    reload()
                }
            }
        )
    }

    Div({ style { property("display", "flex"); property("flex-direction", "column"); property("gap", "1.25rem") } }) {

        // Overview stat cards
        overview?.let { ov ->
            Div({ style {
                property("display", "grid")
                property("grid-template-columns", "repeat(auto-fit, minmax(160px, 1fr))")
                property("gap", "1rem")
            } }) {
                AdminStatCard("Organisationer", ov.totalOrgs.toString(), "var(--rp-pine)", "var(--rp-info-bg)")
                AdminStatCard("Aktiva (betalande)", ov.activeOrgs.toString(), "var(--rp-ok-text)", "var(--rp-ok-bg)")
                AdminStatCard("Trial", ov.trialOrgs.toString(), "var(--rp-warn-text)", "var(--rp-warn-bg)")
                AdminStatCard("Förfallna / Avslutade", (ov.pastDueOrgs + ov.canceledOrgs).toString(), "var(--rp-err-text)", "var(--rp-err-bg)")
                AdminStatCard("Totalt användare", ov.totalUsers.toString(), "var(--rp-pine)", "var(--rp-info-bg)")
            }
        }

        // Main card
        Div({ style { property("background", "var(--rp-surface)"); property("border-radius", "24px"); property("padding", "1.5rem") } }) {

            // Sub-view toggle + search row
            Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "1rem"); property("margin-bottom", "1.25rem"); property("flex-wrap", "wrap") } }) {
                // Toggle
                Div({ style {
                    property("display", "inline-flex"); property("background", "var(--rp-overlay)")
                    property("border-radius", "12px"); property("padding", "3px"); property("gap", "2px")
                } }) {
                    listOf(AdminView.ORGS to "Organisationer", AdminView.USERS to "Användare").forEach { (v, label) ->
                        val active = view == v
                        Button({
                            onClick { view = v; search = "" }
                            style {
                                property("background", if (active) "var(--rp-surface)" else "transparent")
                                property("color", if (active) "var(--rp-pine)" else "var(--rp-subtle)")
                                property("border", "none"); property("border-radius", "9px")
                                property("padding", "0.45rem 0.9rem"); property("cursor", "pointer")
                                property("font-weight", if (active) "700" else "600")
                                property("font-size", "0.85rem"); property("font-family", "inherit")
                                if (active) property("box-shadow", "0 1px 3px rgba(13,13,24,0.08)")
                            }
                        }) { Text(label) }
                    }
                }

                // Search
                Input(InputType.Search) {
                    value(search)
                    onInput { search = it.value }
                    placeholder("Sök…")
                    style {
                        property("padding", "0.5rem 1rem"); property("border", "2px solid var(--rp-hl-med)")
                        property("border-radius", "50px"); property("font-size", "0.9rem")
                        property("outline", "none"); property("flex", "1"); property("min-width", "160px")
                    }
                }

                // Reload button
                Button({
                    onClick { reload() }
                    style {
                        property("padding", "0.5rem 1rem"); property("border", "2px solid var(--rp-hl-med)")
                        property("background", "var(--rp-surface)"); property("border-radius", "50px")
                        property("cursor", "pointer"); property("font-size", "0.85rem"); property("color", "var(--rp-subtle)")
                    }
                }) { I({ classes("fa-solid", "fa-rotate-right") }) }
            }

            error?.let {
                Div({ style { property("color", "var(--rp-err-text)"); property("margin-bottom", "1rem"); property("font-size", "0.9rem") } }) { Text(it) }
            }

            if (loading) {
                Div({ style { property("text-align", "center"); property("padding", "2rem"); property("color", "var(--rp-subtle)") } }) { Text("Laddar…") }
            } else when (view) {
                AdminView.ORGS -> {
                    val filtered = orgs.filter {
                        search.isBlank() || it.name.contains(search, ignoreCase = true) ||
                                it.orgNumber?.contains(search) == true
                    }
                    AdminOrgsTable(
                        orgs = filtered,
                        savingId = savingId,
                        onSetStatus = { orgId, status ->
                            savingId = orgId
                            adminScope.launch {
                                adminRepo.setOrgPlanStatus(orgId, status).onFailure { error = it.message }
                                adminRepo.getOrgs().onSuccess { orgs = it }
                                savingId = null
                            }
                        },
                        onEditOrg = { editingOrg = it },
                        onDeleteOrg = { deletingOrg = it }
                    )
                }
                AdminView.USERS -> {
                    val filtered = users.filter {
                        search.isBlank() ||
                                it.email.contains(search, ignoreCase = true) ||
                                "${it.firstName} ${it.lastName}".contains(search, ignoreCase = true) ||
                                it.orgName?.contains(search, ignoreCase = true) == true
                    }
                    AdminUsersTable(
                        users = filtered,
                        savingId = savingId,
                        onToggleActive = { userId, active ->
                            savingId = userId
                            adminScope.launch {
                                adminRepo.setUserActive(userId, active).onFailure { error = it.message }
                                adminRepo.getUsers().onSuccess { users = it }
                                savingId = null
                            }
                        },
                        onEditUser = { editingUser = it },
                        onDeleteUser = { deletingUser = it }
                    )
                }
            }
        }
    }
}

@Composable
private fun AdminStatCard(label: String, value: String, color: String, bg: String) {
    Div({ style {
        property("background", bg); property("border-radius", "16px"); property("padding", "1.1rem 1.25rem")
    } }) {
        Div({ style { property("font-size", "1.6rem"); property("font-weight", "800"); property("color", color) } }) { Text(value) }
        Div({ style { property("font-size", "0.78rem"); property("font-weight", "600"); property("color", color); property("margin-top", "0.2rem"); property("opacity", "0.75") } }) { Text(label) }
    }
}

@Composable
private fun AdminOrgsTable(
    orgs: List<AdminOrgDto>,
    savingId: Long?,
    onSetStatus: (Long, String) -> Unit,
    onEditOrg: (AdminOrgDto) -> Unit,
    onDeleteOrg: (AdminOrgDto) -> Unit
) {
    if (orgs.isEmpty()) {
        Div({ style { property("text-align", "center"); property("padding", "2rem"); property("color", "var(--rp-muted)") } }) { Text("Inga organisationer hittades.") }
        return
    }
    Div({ style { property("overflow-x", "auto") } }) {
        Table({ style {
            property("width", "100%"); property("border-collapse", "collapse"); property("font-size", "0.9rem")
        } }) {
            Thead {
                Tr {
                    listOf("Organisation", "Org.nr", "Status", "Arbetsplatser", "Användare", "Skapad", "Åtgärder").forEach { col ->
                        Th({ style { adminThStyle() } }) { Text(col) }
                    }
                }
            }
            Tbody {
                orgs.forEach { org ->
                    Tr({
                        style {
                            property("border-bottom", "1px solid var(--rp-hl-med)")
                            property("opacity", if (savingId == org.id) "0.5" else "1")
                        }
                    }) {
                        Td({ style { adminTdStyle() } }) {
                            Span({ style { property("font-weight", "700"); property("color", "var(--rp-text)") } }) { Text(org.name) }
                        }
                        Td({ style { adminTdStyle() } }) { Text(org.orgNumber ?: "–") }
                        Td({ style { adminTdStyle() } }) {
                            AdminPlanStatusSelect(org.id, org.planStatus, savingId, onSetStatus)
                        }
                        Td({ style { adminTdStyle(); property("text-align", "center") } }) { Text(org.workplaceCount.toString()) }
                        Td({ style { adminTdStyle(); property("text-align", "center") } }) { Text(org.userCount.toString()) }
                        Td({ style { adminTdStyle() } }) { Text(epochMsToDate(org.createdAt)) }
                        Td({ style { adminTdStyle(); property("white-space", "nowrap") } }) {
                            Button({
                                onClick { onEditOrg(org) }
                                title("Redigera organisation")
                                style {
                                    property("background", "var(--rp-overlay)"); property("border", "none")
                                    property("border-radius", "8px"); property("padding", "0.4rem 0.6rem")
                                    property("cursor", "pointer"); property("margin-right", "0.5rem")
                                    property("color", "var(--rp-pine)"); property("font-size", "0.85rem")
                                }
                            }) { I({ classes("fa-solid", "fa-pen-to-square") }) }

                            Button({
                                onClick { onDeleteOrg(org) }
                                title("Radera organisation")
                                style {
                                    property("background", "var(--rp-err-bg)"); property("border", "none")
                                    property("border-radius", "8px"); property("padding", "0.4rem 0.6rem")
                                    property("cursor", "pointer")
                                    property("color", "var(--rp-err-text)"); property("font-size", "0.85rem")
                                }
                            }) { I({ classes("fa-solid", "fa-trash-can") }) }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun AdminPlanStatusSelect(orgId: Long, current: String, savingId: Long?, onSetStatus: (Long, String) -> Unit) {
    val (bg, color) = when (current) {
        "active" -> "var(--rp-ok-bg)" to "var(--rp-ok-text)"
        "trial" -> "var(--rp-warn-bg)" to "var(--rp-warn-text)"
        "past_due" -> "var(--rp-err-bg)" to "var(--rp-err-text)"
        "canceled" -> "var(--rp-overlay)" to "var(--rp-subtle)"
        else -> "var(--rp-overlay)" to "var(--rp-subtle)"
    }
    Select({
        onChange { it.value?.let { v -> onSetStatus(orgId, v) } }
        if (savingId == orgId) disabled()
        style {
            property("background", bg); property("color", color)
            property("border", "none"); property("border-radius", "50px")
            property("padding", "0.3rem 0.75rem"); property("font-weight", "700")
            property("font-size", "0.8rem"); property("cursor", "pointer"); property("font-family", "inherit")
        }
    }) {
        listOf("trial" to "Trial", "active" to "Aktiv", "past_due" to "Förfallutet", "canceled" to "Avslutat").forEach { (v, label) ->
            Option(v, { if (v == current) selected() }) { Text(label) }
        }
    }
}

@Composable
private fun AdminUsersTable(
    users: List<AdminUserDto>,
    savingId: Long?,
    onToggleActive: (Long, Boolean) -> Unit,
    onEditUser: (AdminUserDto) -> Unit,
    onDeleteUser: (AdminUserDto) -> Unit
) {
    if (users.isEmpty()) {
        Div({ style { property("text-align", "center"); property("padding", "2rem"); property("color", "var(--rp-muted)") } }) { Text("Inga användare hittades.") }
        return
    }
    Div({ style { property("overflow-x", "auto") } }) {
        Table({ style {
            property("width", "100%"); property("border-collapse", "collapse"); property("font-size", "0.9rem")
        } }) {
            Thead {
                Tr {
                    listOf("Namn", "E-post", "Roll", "Organisation", "Status", "Skapad", "Åtgärder").forEach { col ->
                        Th({ style { adminThStyle() } }) { Text(col) }
                    }
                }
            }
            Tbody {
                users.forEach { user ->
                    Tr({
                        style {
                            property("border-bottom", "1px solid var(--rp-hl-med)")
                            property("opacity", if (savingId == user.userId) "0.5" else "1")
                        }
                    }) {
                        Td({ style { adminTdStyle() } }) {
                            val name = listOfNotNull(user.firstName, user.lastName).joinToString(" ")
                            Span({ style { property("font-weight", "700"); property("color", "var(--rp-text)") } }) {
                                Text(name.ifBlank { "–" })
                            }
                        }
                        Td({ style { adminTdStyle() } }) { Text(user.email) }
                        Td({ style { adminTdStyle() } }) {
                            val (bg, color) = if (user.role == "manager") "var(--rp-info-bg)" to "var(--rp-pine)" else "var(--rp-overlay)" to "var(--rp-subtle)"
                            Span({ style {
                                property("background", bg); property("color", color)
                                property("padding", "0.2rem 0.6rem"); property("border-radius", "50px")
                                property("font-weight", "700"); property("font-size", "0.78rem")
                            } }) { Text(if (user.role == "manager") "Chef" else "Personal") }
                        }
                        Td({ style { adminTdStyle() } }) { Text(user.orgName ?: "–") }
                        Td({ style { adminTdStyle() } }) {
                            Button({
                                onClick { onToggleActive(user.userId, !user.active) }
                                if (savingId == user.userId) disabled()
                                style {
                                    val (bg, color) = if (user.active) "var(--rp-ok-bg)" to "var(--rp-pine)" else "var(--rp-err-bg)" to "var(--rp-love)"
                                    property("background", bg); property("color", color)
                                    property("border", "none"); property("border-radius", "50px")
                                    property("padding", "0.3rem 0.75rem"); property("font-weight", "700")
                                    property("font-size", "0.8rem"); property("cursor", "pointer"); property("font-family", "inherit")
                                }
                            }) { Text(if (user.active) "Aktiv" else "Inaktiv") }
                        }
                        Td({ style { adminTdStyle() } }) { Text(epochMsToDate(user.createdAt)) }
                        Td({ style { adminTdStyle(); property("white-space", "nowrap") } }) {
                            Button({
                                onClick { onEditUser(user) }
                                title("Redigera användare")
                                style {
                                    property("background", "var(--rp-overlay)"); property("border", "none")
                                    property("border-radius", "8px"); property("padding", "0.4rem 0.6rem")
                                    property("cursor", "pointer"); property("margin-right", "0.5rem")
                                    property("color", "var(--rp-pine)"); property("font-size", "0.85rem")
                                }
                            }) { I({ classes("fa-solid", "fa-pen-to-square") }) }

                            Button({
                                onClick { onDeleteUser(user) }
                                title("Radera användare")
                                style {
                                    property("background", "var(--rp-err-bg)"); property("border", "none")
                                    property("border-radius", "8px"); property("padding", "0.4rem 0.6rem")
                                    property("cursor", "pointer")
                                    property("color", "var(--rp-err-text)"); property("font-size", "0.85rem")
                                }
                            }) { I({ classes("fa-solid", "fa-trash-can") }) }
                        }
                    }
                }
            }
        }
    }
}

private fun org.jetbrains.compose.web.css.StyleScope.adminThStyle() {
    property("text-align", "left"); property("padding", "0.6rem 0.75rem")
    property("font-size", "0.75rem"); property("font-weight", "700"); property("color", "var(--rp-subtle)")
    property("text-transform", "uppercase"); property("letter-spacing", "0.5px")
    property("border-bottom", "2px solid var(--rp-hl-med)"); property("white-space", "nowrap")
}

private fun org.jetbrains.compose.web.css.StyleScope.adminTdStyle() {
    property("padding", "0.75rem"); property("color", "var(--rp-text)"); property("vertical-align", "middle")
}

private fun epochMsToDate(ms: Long): String {
    val d = kotlin.js.Date(ms.toDouble())
    val y = d.getFullYear()
    val m = (d.getMonth() + 1).toString().padStart(2, '0')
    val day = d.getDate().toString().padStart(2, '0')
    return "$y-$m-$day"
}

@Composable
private fun AdminEditOrgModal(
    org: AdminOrgDto,
    onDismiss: () -> Unit,
    onSave: (name: String, orgNumber: String?) -> Unit
) {
    var name by remember { mutableStateOf(org.name) }
    var orgNumber by remember { mutableStateOf(org.orgNumber ?: "") }

    AdminModalBackdrop(onDismiss) {
        H3({ style { property("margin-top", "0"); property("margin-bottom", "1.25rem"); property("color", "var(--rp-text)") } }) {
            Text("Redigera organisation")
        }

        Div({ style { property("display", "flex"); property("flex-direction", "column"); property("gap", "1rem") } }) {
            Div {
                Span({ style { property("display", "block"); property("font-size", "0.85rem"); property("font-weight", "600"); property("margin-bottom", "0.3rem") } }) {
                    Text("Organisationsnamn")
                }
                Input(InputType.Text) {
                    value(name)
                    onInput { name = it.value }
                    style { adminInputStyle() }
                }
            }

            Div {
                Span({ style { property("display", "block"); property("font-size", "0.85rem"); property("font-weight", "600"); property("margin-bottom", "0.3rem") } }) {
                    Text("Organisationsnummer")
                }
                Input(InputType.Text) {
                    value(orgNumber)
                    onInput { orgNumber = it.value }
                    placeholder("55XXXX-XXXX")
                    style { adminInputStyle() }
                }
            }

            Div({ style { property("display", "flex"); property("justify-content", "flex-end"); property("gap", "0.75rem"); property("margin-top", "1rem") } }) {
                Button({
                    onClick { onDismiss() }
                    style { adminCancelBtnStyle() }
                }) { Text("Avbryt") }

                Button({
                    onClick { if (name.isNotBlank()) onSave(name.trim(), orgNumber.trim().ifBlank { null }) }
                    style { adminPrimaryBtnStyle() }
                }) { Text("Spara") }
            }
        }
    }
}

@Composable
private fun AdminEditUserModal(
    user: AdminUserDto,
    onDismiss: () -> Unit,
    onSave: (firstName: String?, lastName: String?, email: String, role: String) -> Unit
) {
    var firstName by remember { mutableStateOf(user.firstName ?: "") }
    var lastName by remember { mutableStateOf(user.lastName ?: "") }
    var email by remember { mutableStateOf(user.email) }
    var role by remember { mutableStateOf(user.role) }

    AdminModalBackdrop(onDismiss) {
        H3({ style { property("margin-top", "0"); property("margin-bottom", "1.25rem"); property("color", "var(--rp-text)") } }) {
            Text("Redigera användare")
        }

        Div({ style { property("display", "flex"); property("flex-direction", "column"); property("gap", "1rem") } }) {
            Div({ style { property("display", "flex"); property("gap", "0.75rem") } }) {
                Div({ style { property("flex", "1") } }) {
                    Span({ style { property("display", "block"); property("font-size", "0.85rem"); property("font-weight", "600"); property("margin-bottom", "0.3rem") } }) {
                        Text("Förnamn")
                    }
                    Input(InputType.Text) {
                        value(firstName)
                        onInput { firstName = it.value }
                        style { adminInputStyle() }
                    }
                }
                Div({ style { property("flex", "1") } }) {
                    Span({ style { property("display", "block"); property("font-size", "0.85rem"); property("font-weight", "600"); property("margin-bottom", "0.3rem") } }) {
                        Text("Efternamn")
                    }
                    Input(InputType.Text) {
                        value(lastName)
                        onInput { lastName = it.value }
                        style { adminInputStyle() }
                    }
                }
            }

            Div {
                Span({ style { property("display", "block"); property("font-size", "0.85rem"); property("font-weight", "600"); property("margin-bottom", "0.3rem") } }) {
                    Text("E-post")
                }
                Input(InputType.Email) {
                    value(email)
                    onInput { email = it.value }
                    style { adminInputStyle() }
                }
            }

            Div {
                Span({ style { property("display", "block"); property("font-size", "0.85rem"); property("font-weight", "600"); property("margin-bottom", "0.3rem") } }) {
                    Text("Roll")
                }
                Select({
                    onChange { it.value?.let { v -> role = v } }
                    style { adminInputStyle() }
                }) {
                    Option("manager", { if (role == "manager") selected() }) { Text("Chef (manager)") }
                    Option("staff", { if (role == "staff") selected() }) { Text("Personal (staff)") }
                }
            }

            Div({ style { property("display", "flex"); property("justify-content", "flex-end"); property("gap", "0.75rem"); property("margin-top", "1rem") } }) {
                Button({
                    onClick { onDismiss() }
                    style { adminCancelBtnStyle() }
                }) { Text("Avbryt") }

                Button({
                    onClick { if (email.isNotBlank()) onSave(firstName.trim().ifBlank { null }, lastName.trim().ifBlank { null }, email.trim(), role) }
                    style { adminPrimaryBtnStyle() }
                }) { Text("Spara") }
            }
        }
    }
}

@Composable
private fun AdminConfirmDeleteModal(
    title: String,
    message: String,
    confirmKeyword: String,
    onDismiss: () -> Unit,
    onConfirm: () -> Unit
) {
    var typedInput by remember { mutableStateOf("") }
    val isConfirmed = typedInput.trim().equals(confirmKeyword.trim(), ignoreCase = true)

    AdminModalBackdrop(onDismiss) {
        H3({ style { property("margin-top", "0"); property("margin-bottom", "0.75rem"); property("color", "var(--rp-err-text)") } }) {
            Text(title)
        }

        P({ style { property("color", "var(--rp-subtle)"); property("font-size", "0.95rem"); property("line-height", "1.5"); property("margin-bottom", "1rem") } }) {
            Text(message)
        }

        P({ style { property("font-size", "0.85rem"); property("font-weight", "600"); property("color", "var(--rp-text)"); property("margin-bottom", "0.4rem") } }) {
            Text("Skriv in \"$confirmKeyword\" nedan för att bekräfta radering:")
        }

        Input(InputType.Text) {
            value(typedInput)
            onInput { typedInput = it.value }
            placeholder(confirmKeyword)
            style {
                adminInputStyle()
                property("border-color", if (isConfirmed) "var(--rp-ok-border)" else "var(--rp-hl-med)")
            }
        }

        Div({ style { property("display", "flex"); property("justify-content", "flex-end"); property("gap", "0.75rem"); property("margin-top", "1.25rem") } }) {
            Button({
                onClick { onDismiss() }
                style { adminCancelBtnStyle() }
            }) { Text("Avbryt") }

            Button({
                onClick { if (isConfirmed) onConfirm() }
                if (!isConfirmed) disabled()
                style {
                    property("background", if (isConfirmed) "var(--rp-love)" else "var(--rp-err-border)")
                    property("color", "white"); property("border", "none")
                    property("border-radius", "8px"); property("padding", "0.55rem 1.25rem")
                    property("font-weight", "700"); property("font-size", "0.9rem")
                    property("cursor", if (isConfirmed) "pointer" else "not-allowed")
                }
            }) { Text("Radera permanent") }
        }
    }
}

@Composable
private fun AdminModalBackdrop(onDismiss: () -> Unit, content: @Composable () -> Unit) {
    Div({
        onClick { onDismiss() }
        style {
            property("position", "fixed"); property("inset", "0"); property("z-index", "1000")
            property("background", "rgba(13,13,24,0.55)"); property("backdrop-filter", "blur(4px)")
            property("display", "flex"); property("align-items", "center"); property("justify-content", "center")
            property("padding", "1rem")
        }
    }) {
        Div({
            onClick { it.stopPropagation() }
            style {
                property("background", "var(--rp-surface)"); property("border-radius", "20px")
                property("padding", "1.75rem"); property("width", "100%"); property("max-width", "480px")
                property("box-shadow", "0 20px 60px rgba(13,13,24,0.2)")
                property("position", "relative")
            }
        }) {
            content()
        }
    }
}

private fun org.jetbrains.compose.web.css.StyleScope.adminInputStyle() {
    property("width", "100%"); property("padding", "0.55rem 0.85rem")
    property("border", "1.5px solid var(--rp-hl-med)"); property("border-radius", "10px")
    property("font-size", "0.9rem"); property("outline", "none")
    property("box-sizing", "border-box"); property("font-family", "inherit")
}

private fun org.jetbrains.compose.web.css.StyleScope.adminCancelBtnStyle() {
    property("background", "var(--rp-overlay)"); property("color", "var(--rp-subtle)")
    property("border", "none"); property("border-radius", "8px")
    property("padding", "0.55rem 1.1rem"); property("font-weight", "600")
    property("cursor", "pointer"); property("font-size", "0.9rem")
}

private fun org.jetbrains.compose.web.css.StyleScope.adminPrimaryBtnStyle() {
    property("background", "var(--rp-pine)"); property("color", "white")
    property("border", "none"); property("border-radius", "8px")
    property("padding", "0.55rem 1.25rem"); property("font-weight", "700")
    property("cursor", "pointer"); property("font-size", "0.9rem")
}
